import React, { FC, Fragment } from 'react';
import { LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { ClientDirInvoiceEmailSettingsFormData } from 'models/forms';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { emptyInvoiceEmailSettingsData } from './form-helper';
import { useFormik } from 'formik';
import { Box, FormControl, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import ChipInput from 'components/shared/chip-input/chip-input.component';
import { isValidEmail } from 'utils/validations';
import { InvoiceDelivery, InvoicingType } from 'models/invoice';

type EmailSettingsFormProps = {
    labClient: LabClient;
    onChange: (formData: ClientDirInvoiceEmailSettingsFormData, isFormValid: boolean) => void;
    clinicForcedInvoicingType?: InvoicingType;
    clinicForcedInvoiceDelivery?: InvoiceDelivery;
};
const EmailSettingsForm: FC<EmailSettingsFormProps> = ({
    labClient,
    onChange,
    clinicForcedInvoicingType,
    clinicForcedInvoiceDelivery,
}: EmailSettingsFormProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'invoices', 'organization']);
    const currentOrg = currentOrgVar();
    const [isEmailChipsValid, setIsEmailChipsValid] = React.useState({ to: true, cc: true, bcc: true });

    const validate = (formData: ClientDirInvoiceEmailSettingsFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.to || formData.to.length === 0) {
            errors.to = 'INVALID EMAIL';
        }
        if (!isEmailChipsValid.to || !isEmailChipsValid.cc || !isEmailChipsValid.bcc) {
            errors.to = 'INVALID EMAIL';
        }
        if (!formData.replyAddress) {
            errors.replyAddress = translate('client-directory:VALIDATION:REPLY_ADDRESS_NOT_SET');
        }
        if (formData.replyAddress && !isValidEmail(formData.replyAddress)) {
            errors.replyAddress = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        if (!formData.body) {
            errors.body = translate('client-directory:VALIDATION:EMAIL_BODY_NOT_SET');
        }
        if (!formData.creditBody) {
            errors.creditBody = translate('client-directory:VALIDATION:EMAIL_BODY_NOT_SET');
        }
        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    const formik = useFormik<ClientDirInvoiceEmailSettingsFormData>({
        initialValues: emptyInvoiceEmailSettingsData(labClient, currentOrg),
        validateOnMount: true,
        enableReinitialize: true,
        validate,
        onSubmit: async (formData: ClientDirInvoiceEmailSettingsFormData) => {
            onChange(formData, formik.isValid);
        },
    });

    const handleEmailChipChange = (field: 'to' | 'cc' | 'bcc', value: string[]) => {
        setIsEmailChipsValid({ ...isEmailChipsValid, [field]: true });
        formik.setFieldValue(field, value);
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };

    return (
        <Fragment>
            <Box>
                <Typography variant="h4" sx={{ marginBottom: 2 }}>
                    {translate('client-directory:EMAIL_SETTINGS')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item sm={12} xs={12}>
                        <FormControl fullWidth>
                            <ChipInput
                                required
                                label={translate('invoices:TO')}
                                placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                options={[]}
                                disabled={
                                    (clinicForcedInvoicingType === InvoicingType.COLLECTIVE ||
                                        clinicForcedInvoicingType === InvoicingType.COLLECTIVE_DENTIST) &&
                                    clinicForcedInvoiceDelivery === InvoiceDelivery.EMAIL &&
                                    !!labClient?.customer?.invoicing?.communication?.email?.length
                                }
                                errorMessage={translate('common:VALIDATION:TO')}
                                createChipOnBlur
                                chipValidator={isValidEmail}
                                defaultValue={formik.values.to || []}
                                onValueChange={(value) => handleEmailChipChange('to', value)}
                                onDelete={(value) => handleEmailChipChange('to', value)}
                                onError={(error) => {
                                    setIsEmailChipsValid({ ...isEmailChipsValid, to: !error });
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={4} xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                required
                                label={translate('client-directory:REPLY_ADDRESS')}
                                helperText={formik.touched.replyAddress && formik.errors.replyAddress}
                                error={formik.touched.replyAddress && Boolean(formik.errors.replyAddress)}
                                {...formik.getFieldProps('replyAddress')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={4} xs={12}>
                        <FormControl fullWidth>
                            <ChipInput
                                label={translate('invoices:CC')}
                                placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                options={[]}
                                createChipOnBlur
                                chipValidator={isValidEmail}
                                defaultValue={formik.values.cc || []}
                                onValueChange={(value) => handleEmailChipChange('cc', value)}
                                onDelete={(value) => handleEmailChipChange('cc', value)}
                                onError={(error) => {
                                    setIsEmailChipsValid({ ...isEmailChipsValid, cc: !error });
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={4} xs={12}>
                        <FormControl fullWidth>
                            <ChipInput
                                label={translate('invoices:BCC')}
                                placeholder={translate('invoices:EMAIL_PLACEHOLDER')}
                                options={[]}
                                createChipOnBlur
                                chipValidator={isValidEmail}
                                defaultValue={formik.values.bcc || []}
                                onValueChange={(value) => handleEmailChipChange('bcc', value)}
                                onDelete={(value) => handleEmailChipChange('bcc', value)}
                                onError={(error) => {
                                    setIsEmailChipsValid({ ...isEmailChipsValid, bcc: !error });
                                }}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box mt={3}>
                <Typography variant="h4" sx={{ marginBottom: 2 }}>
                    {translate('client-directory:DEBIT_EMAIL_CONTENT')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                label={translate('invoices:SUBJECT')}
                                {...formik.getFieldProps('subject')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {translate('orders:INVOICE')} 10000
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                required
                                multiline
                                rows={4}
                                label={translate('invoices:EMAIL_BODY')}
                                helperText={formik.touched.body && formik.errors.body}
                                error={formik.touched.body && Boolean(formik.errors.body)}
                                {...formik.getFieldProps('body')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box mt={3}>
                <Typography variant="h4" sx={{ marginBottom: 2 }}>
                    {translate('client-directory:CREDIT_EMAIL_CONTENT')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                label={translate('invoices:SUBJECT')}
                                {...formik.getFieldProps('creditSubject')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {translate('invoices:CREDIT_INVOICE')} 10000
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                required
                                multiline
                                rows={4}
                                label={translate('invoices:EMAIL_BODY')}
                                helperText={formik.touched.creditBody && formik.errors.creditBody}
                                error={formik.touched.creditBody && Boolean(formik.errors.creditBody)}
                                {...formik.getFieldProps('creditBody')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    );
};

export default EmailSettingsForm;
